import { useQuery } from '@hooks/reactQuery';

import { getOrganizationDashboardRequestStatusesSummary } from '@api/dashboardPO';

import { IId } from '@interfaces/common/common';
import { TSortedDataParams } from '@interfaces/common/response';
import { ISort } from '@interfaces/common/store';
import { IDashboardRequestStatusSummary } from '@interfaces/platform/platformDashboard';

import { ECommonRequestStatusesForAssetRequest, TCommonRequestStatusesForAssetRequest } from '@enums/asset';

import { refetchOptions } from '@constants/common';
import { DASHBOARD_REQUEST_STATUSES_SUMMARY_ORGANIZATION } from '@constants/queryKeys';

function getStatusOrder(enumValue: TCommonRequestStatusesForAssetRequest): number {
	const enumIndex = Object.values(ECommonRequestStatusesForAssetRequest).indexOf(enumValue);

	return enumIndex !== -1 ? enumIndex : 0;
}

const getSortedData = ({
	data,
	sortDirection,
	sortFieldName,
}: TSortedDataParams<IDashboardRequestStatusSummary>) => {
	if (!data) return [];

	if (!sortFieldName) return data.filter(item => !!item);

	return [...data].sort((a, b) => {
		const [first, second] = sortDirection ? [a, b] : [b, a];

		switch (sortFieldName) {
			case 'status':
				return getStatusOrder(first.status.name) - getStatusOrder(second.status.name);
			case 'organizationCount':
				return first.organizationCount - second.organizationCount;
			case 'requestCount':
				return first.requestCount - second.requestCount;
			default:
				return 0;
		}
	}).filter(item => !!item);
};

type TUseQueryOrganizationDashboardRequestStatusesSummary = ISort & IId<string | undefined>

const useQueryOrganizationDashboardRequestStatusesSummary = ({
	sortDirection,
	sortFieldName,
	id,
}: TUseQueryOrganizationDashboardRequestStatusesSummary) => {
	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [DASHBOARD_REQUEST_STATUSES_SUMMARY_ORGANIZATION, id],
		queryFn: () => getOrganizationDashboardRequestStatusesSummary(id!),
		enabled: [!!id],
		...refetchOptions,
	});

	const organizationDashboardRequestStatusesSummary = getSortedData({
		data,
		sortDirection,
		sortFieldName,
	});

	return {
		organizationDashboardRequestStatusesSummary,
		isLoadingOrganizationDashboardRequestStatusesSummary: isLoading || isFetching,
	};
};

export default useQueryOrganizationDashboardRequestStatusesSummary;
